import { forwardRef, useId } from 'react';
import type { SelectHTMLAttributes } from 'react';
import type { FieldError } from 'react-hook-form';
import { cn } from '@/lib/utils';

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  error?: FieldError;
  options: Array<{ value: string; label: string }>;
  containerClassName?: string;
  /**
   * 'default' — legacy 2px-radius select.
   * 'lms' — Figma "LMS Portal - Updated" select, matching FormField's lms
   * input: N600 label, rounded-8 Card/Stroke 3 border, 16/24 text.
   */
  variant?: 'default' | 'lms';
  /** Visually hide the label (kept for screen readers) — for in-table use. */
  hideLabel?: boolean;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  (
    {
      label,
      error,
      options,
      containerClassName = '',
      className = '',
      variant = 'default',
      hideLabel = false,
      id,
      ...props
    },
    ref
  ) => {
    // Associate label and select (clickable label, reachable by label text).
    const generatedId = useId();
    const selectId = id ?? generatedId;

    const labelClasses =
      variant === 'lms'
        ? 'block mb-1.5 text-body-regular text-neutral-n600'
        : 'block mb-1 text-body-regular text-character-title-85';

    const selectClasses =
      variant === 'lms'
        ? 'w-full outline-none font-roboto text-[16px] leading-6 text-black rounded-lg border border-lms-border bg-white px-4 py-3 focus:border-primary-6 transition-colors disabled:bg-lms-disabled-bg disabled:text-neutral-n400 disabled:cursor-not-allowed'
        : 'w-full outline-none text-body-regular text-character-title-85 rounded-[2px] border border-neutral-100 bg-neutral-0 px-3 py-2';

    return (
      <div className={containerClassName}>
        <label
          htmlFor={selectId}
          className={cn(labelClasses, hideLabel && 'sr-only')}
        >
          {label}
          {variant === 'lms' && props.required && (
            <span className="text-lms-red-hover"> *</span>
          )}
        </label>
        <select
          ref={ref}
          id={selectId}
          className={cn(selectClasses, className)}
          {...props}
        >
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {error && (
          <p className="mt-1 text-footnote-description text-error-500">
            {error.message}
          </p>
        )}
      </div>
    );
  }
);

SelectField.displayName = 'SelectField';
